import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiInbox, FiPlus, FiSearch, FiFilter, FiExternalLink, FiCalendar, FiEdit2, FiTrash2, FiCheck, FiX, FiMoreVertical } = FiIcons;

const MottatteSaker = () => {
  const { user } = useAuth();
  const { utrederInnboks, addUtrederInnboksItem, updateUtrederInnboksItem, deleteUtrederInnboksItem } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    reference_link: '',
    received_date: new Date().toISOString().split('T')[0],
    status: 'Ny',
    notes: ''
  });

  // Filter items for current user (or demo user)
  const myItems = utrederInnboks.filter(item => {
    // For demo simplicity, we show all if createdBy matches or if it's the default demo item
    const isMine = item.createdBy === user?.id || item.createdBy === 'utreder-1';
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return isMine && matchesSearch && matchesStatus;
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        company_name: item.company_name || '',
        reference_link: item.reference_link || '',
        received_date: item.received_date || new Date().toISOString().split('T')[0],
        status: item.status || 'Ny',
        notes: item.notes || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        company_name: '',
        reference_link: '',
        received_date: new Date().toISOString().split('T')[0],
        status: 'Ny',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingItem) {
      updateUtrederInnboksItem(editingItem.id, formData);
    } else {
      addUtrederInnboksItem({ ...formData, createdBy: user?.id || 'utreder-1' });
    }
    setShowModal(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Er du sikker på at du vil slette denne saken?')) {
      deleteUtrederInnboksItem(id);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Ny': return 'bg-blue-100 text-blue-800';
      case 'Pågår': return 'bg-yellow-100 text-yellow-800';
      case 'Avsluttet': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={FiInbox} className="text-indigo-600" /> Mottatte saker
            </h1>
            <p className="text-gray-500 text-sm">Oversikt over saker mottatt via andre kanaler (e-post, telefon, brev).</p>
          </div>
          <button 
            onClick={() => handleOpenModal()} 
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 font-medium shadow-sm"
          >
            <SafeIcon icon={FiPlus} /> Registrer ny sak
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Søk i tittel eller bedrift..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFilter} className="text-gray-400" />
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Alle statuser</option>
              <option value="Ny">Ny</option>
              <option value="Pågår">Pågår</option>
              <option value="Avsluttet">Avsluttet</option>
            </select>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold">
              <tr>
                <th className="px-6 py-4">Tittel</th>
                <th className="px-6 py-4">Bedrift</th>
                <th className="px-6 py-4">Mottatt Dato</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Notat</th>
                <th className="px-6 py-4 text-right">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {myItems.map(item => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">{item.title}</p>
                    {item.reference_link && (
                      <a href={item.reference_link} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mt-1">
                        <SafeIcon icon={FiExternalLink} /> Åpne lenke
                      </a>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{item.company_name || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 flex items-center gap-2">
                    <SafeIcon icon={FiCalendar} className="text-gray-400" /> 
                    {new Date(item.received_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={item.notes}>
                    {item.notes || '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleOpenModal(item)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                        <SafeIcon icon={FiEdit2} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {myItems.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <SafeIcon icon={FiInbox} className="mx-auto text-4xl text-gray-300 mb-2" />
                    <p>Ingen saker funnet i innboksen.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">{editingItem ? 'Rediger sak' : 'Registrer ny sak'}</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tittel *</label>
                  <input 
                    type="text" 
                    required 
                    value={formData.title} 
                    onChange={e => setFormData({...formData, title: e.target.value})} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    placeholder="F.eks. Varsel fra e-post"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bedriftsnavn</label>
                    <input 
                      type="text" 
                      value={formData.company_name} 
                      onChange={e => setFormData({...formData, company_name: e.target.value})} 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mottatt dato</label>
                    <input 
                      type="date" 
                      value={formData.received_date} 
                      onChange={e => setFormData({...formData, received_date: e.target.value})} 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referanselenke (URL)</label>
                  <input 
                    type="url" 
                    value={formData.reference_link} 
                    onChange={e => setFormData({...formData, reference_link: e.target.value})} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select 
                    value={formData.status} 
                    onChange={e => setFormData({...formData, status: e.target.value})} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="Ny">Ny</option>
                    <option value="Pågår">Pågår</option>
                    <option value="Avsluttet">Avsluttet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notater</label>
                  <textarea 
                    value={formData.notes} 
                    onChange={e => setFormData({...formData, notes: e.target.value})} 
                    rows="4" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    placeholder="Interne notater..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Avbryt</button>
                  <button type="submit" className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
                    <SafeIcon icon={FiCheck} /> Lagre
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MottatteSaker;